import { useMutation, useQuery } from 'react-query';
import { MutableRefObject, useEffect, useRef } from 'react';
import axios from 'axios';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { LineString } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import WKT from 'ol/format/WKT';

import { ILineString } from 'features/seismic/models/interfaces/ILineString';
import { IWell } from 'features/seismic/models/interfaces/IWell';
import { useSessionStore } from 'session/useSessionStore';
import { IResponseAPI } from 'models/interfaces/IResponseAPI';
import { DirectionalPathPointsViewModel } from 'features/seismic/models/interfaces/DirectionalPathPointsViewModel';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { useLine3DNavigationStore } from 'features/seismic/stores/useLine3DNavigationStore';
import { useMapStore } from 'features/seismic/stores/useMapStore';
import { LineType } from 'features/seismic/models/enums/LineType';
import { calculateXPosition } from 'features/seismic/utils/calculateScreenValuesUtils';
import { createDirectionalPathSegment, createTextFeature, getYValue } from 'features/seismic/utils/wellsUtils';
import { TenantConfig } from 'features/tenant/model/TenantConfig';

function useDirectionalPath(wellInfo: IWell, dataGeometryLine: ILineString | null, domain: number, datum: number, tenantConfig: TenantConfig | undefined, jwtToken: string, srid: number|undefined|null) {
    return useMutation(async () => {
        const response = await axios.post<IResponseAPI<DirectionalPathPointsViewModel>>(
            tenantConfig?.endpoints.wells + '/Wells/SeismicWells/GetDirectionalPathForSeismic2D',
            {
                wellId: wellInfo.Id,
                Trace: dataGeometryLine?.TotalTraces,
                Domain: domain,
                Datum: datum,
                LineString: dataGeometryLine?.Line,
                Srid: srid
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`
                }
            }
        );
        return response.data.Result;
    });
};

export function useGetDirectionalPath (checked: boolean, loading: MutableRefObject<boolean>, wellInfo: IWell, domain: number, datum: number, dataGeometryLine: ILineString | null) {
    const {tenantConfig, jwtToken, srid} = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
        jwtToken: state.jwtToken,
        srid: state.tenantConfiguration?.miniMapSrid
    }));

    const { mutateAsync: getDirectionalPathData, isError, isLoading } = useDirectionalPath(wellInfo, dataGeometryLine, domain, datum, tenantConfig, jwtToken, srid);

    const { surveyMetadata, wellLayers, setWellLayers, scale, calculator, isInverted, setMiniMapWells, setCreateWellInMiniMap, setRemoveWellInMiniMap, miniMapWells } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
        wellLayers: state.wellsLayers,
        setWellLayers: state.setWellsLayers,
        scale: state.scale,
        calculator: state.calculator,
        isInverted: state.isInverted,
        setMiniMapWells: state.setMiniMapWells,
        setCreateWellInMiniMap: state.setCreateWellInMiniMap,
        setRemoveWellInMiniMap: state.setRemoveWellInMiniMap,
        miniMapWells: state.miniMapWells
    }));

    const { lineType } = useLine3DNavigationStore(state => ({
        lineType: state.lineType
    }));

    const { map } = useMapStore(state => ({
        map: state.map,
    }));

    useEffect(() => {
        if (isLoading) {
            loading.current = true;
        }
    }, [isLoading]);

    useEffect(() => {
        verifyScale.current = { x: scale.x, y: scale.y };
    }, [scale]);

    const verifyScale = useRef<{ x: number, y: number }>({x: scale.x, y: scale.y});

    const directionalPathLine = (checked: boolean, onError: () => void, changed: boolean = false, recreate: boolean = false, zoom: boolean = false) => {
        if ((domain !== null || domain !== undefined) && checked && surveyMetadata) {
            if (!wellLayers[(wellInfo?.Id).toString()]) {
                getDirectionalPathData()
                    .then(directionalPathData => {
                        createDirectionalPath(directionalPathData, zoom);
                        createPointInMiniMap(directionalPathData);
                    });
            }
            else if (!wellLayers[(wellInfo.Id).toString()]['directionalPath'] || recreate) {
                getDirectionalPathData()
                    .then(directionalPathData => {
                        createDirectionalPath(directionalPathData, zoom);
                    });
            }
            else {
                let layer = wellLayers[(wellInfo.Id).toString()]['directionalPath'].layer;
                setWellLayers((wellInfo.Id).toString(), { directionalPath: { layer: layer, checked: true, zoom: wellLayers[(wellInfo.Id).toString()]['directionalPath'].zoom } });
                if (changed) {
                    map?.addLayer(layer);
                    setCreateWellInMiniMap([miniMapWells[(wellInfo.Id).toString()]]);
                }

                if (zoom) {
                    map?.getView().setCenter(wellLayers[(wellInfo.Id).toString()]['directionalPath'].zoom);
                }
            }
        }
        else if ((domain !== null || domain !== undefined) && !checked && surveyMetadata && wellInfo) {
            let layer = wellLayers[(wellInfo.Id).toString()]?.['directionalPath']?.layer;
            if (layer) {
                map?.removeLayer(wellLayers[(wellInfo.Id).toString()]['directionalPath'].layer);
                setWellLayers((wellInfo.Id).toString(), { directionalPath: { layer: layer, checked: false, zoom: wellLayers[(wellInfo.Id).toString()]['directionalPath'].zoom } });
                setRemoveWellInMiniMap([miniMapWells[(wellInfo.Id).toString()]]);
            }
        }

        return null;
    };

    const createDirectionalPath = async (directionalPathData: DirectionalPathPointsViewModel, zoom: boolean) => {
        if (directionalPathData && verifyScale.current.x === scale.x && verifyScale.current.y === scale.y && dataGeometryLine) {
            let layer = new VectorLayer({ source: new VectorSource() });
            map?.addLayer(layer);
            layer.getSource()?.clear();

            let coords = [];
            for (let i = 0; i < directionalPathData.Coordinates.length; i++) {
                if (directionalPathData.Coordinates[i].YPosition > 0) {
                    directionalPathData.Coordinates[i].YPosition *= -1;
                }

                const increment = lineType === LineType.Inline ? surveyMetadata!.Survey3DInfo.XlineIncrement : surveyMetadata!.Survey3DInfo.InlineIncrement;
                const surveyStart = calculator!.getMinTrace(surveyMetadata!);
                const surveyEnd = calculator!.getMaxTrace(surveyMetadata!);
                const totalTracesSurvey = calculator!.getTotalTraces(surveyMetadata!);

                let realTraceFirstPoint =  directionalPathData.Coordinates[i].XPosition;
                if (increment > 1) {
                    realTraceFirstPoint =  ((directionalPathData.Coordinates[i].XPosition)) - ((dataGeometryLine.Min - surveyStart) / increment);
                }
                const directionalPathX = calculateXPosition(realTraceFirstPoint, totalTracesSurvey, surveyStart, surveyEnd, increment, dataGeometryLine.Min, isInverted, scale.x);
                const directionalPathY = getYValue(directionalPathData.Coordinates[i].YPosition, scale.y, calculator!.calculateSampleInterval(surveyMetadata!));

                coords.push([directionalPathX, directionalPathY]);
            }
            createDirectionalPathSegment(coords, layer);

            const features = layer.getSource()?.getFeatures();

            if (features && features.length > 0) {
                let allCoordinates: number[][] = [];

                features.forEach((feature: Feature) => {
                    const geom = feature.getGeometry();
                    if (geom instanceof LineString) {
                        allCoordinates = allCoordinates.concat(geom.getCoordinates());
                    }
                });

                if (allCoordinates.length > 2) {
                    allCoordinates = allCoordinates.sort((a, b) => b[1] - a[1]);

                    const start = allCoordinates[0];
                    const middle = allCoordinates[Math.floor(allCoordinates.length / 2)];
                    const end = allCoordinates[allCoordinates.length - 1];

                    layer.getSource()?.addFeature(createTextFeature(start[0] - 75, start[1], directionalPathData.Labels[0], '#ffffff', 'center', 0, -15));
                    layer.getSource()?.addFeature(createTextFeature(middle[0] - 75, middle[1], directionalPathData.Labels[1], '#ffffff', 'left', 0, 0));
                    layer.getSource()?.addFeature(createTextFeature(end[0] - 75, end[1], (Number((directionalPathData.Labels[2]).replace(',', '.')) - directionalPathData.RotativeDeskInTime).toFixed(2).toString(), '#ffffff', 'center', 0, 15));
                }
            }

            layer.setZIndex(99999990);
            setWellLayers((directionalPathData.Id).toString(), { directionalPath: { layer: layer, checked: true, lineString: directionalPathData.LineString, zoom: [directionalPathData.Coordinates[0].XPosition, -850] } });

            if (zoom) {
                map?.getView().setCenter([directionalPathData.Coordinates[0].XPosition, -850]);
            }
        }

        loading.current = false;
    };

    const createPointInMiniMap = (directionalPathData: DirectionalPathPointsViewModel) => {
        if (directionalPathData) {
            let layer = new VectorLayer({ source: new VectorSource() });
            map?.addLayer(layer);
            layer.getSource()?.clear();

            let coords = [];
            for (let i = 0; i < directionalPathData.Coordinates.length; i++) {
                const wkt = new WKT();

                const point = wkt.readGeometry(directionalPathData.Coordinates[i].Geom);

                if (point.getType() === 'Point') {
                    const coord = (point as unknown as ol.geom.Point).getCoordinates();
                    coords.push(coord);
                }
                createDirectionalPathSegment(coords, layer);
            }

            layer.setZIndex(9999994);
            setMiniMapWells({ [(wellInfo.Id).toString()]: layer });
            setCreateWellInMiniMap([layer]);
        }
    };

    return { directionalPathLine: directionalPathLine, isErrorDirectionalPath: isError };
}